import React, { useEffect, useMemo, useState } from "react";
import { Bot, BrainCircuit, CloudCog, Coins, Image, KeyRound, Layers3, MonitorDown, Network, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { desktopReleaseApi, type DesktopDownloadTarget } from "../../services/desktop-release.api";
import { DownloadSection } from "./DownloadSection";
import { DOWNLOAD_TARGETS } from "./downloadTargets";
import { dedupeDownloadOptions, detectDownloadTarget, pickDownloadOption, sameTarget } from "./downloadUtils";
import { HomeFooter } from "./HomeFooter";
import { HomeHeader } from "./HomeHeader";
import { HomeHero } from "./HomeHero";
import { HomeSections } from "./HomeSections";
import type { DesktopDownloadOption, DownloadState } from "./types";

const HomePage: React.FC = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const location = useLocation();
  const [downloadLoading, setDownloadLoading] = useState(true);
  const [downloadError, setDownloadError] = useState(false);
  const [downloadOptions, setDownloadOptions] = useState<DesktopDownloadOption[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<DesktopDownloadTarget>(() => detectDownloadTarget());
  const capabilities = [
    { icon: MonitorDown, title: t("home.capabilityInstallTitle"), description: t("home.capabilityInstallDescription") },
    { icon: BrainCircuit, title: t("home.capabilityModelTitle"), description: t("home.capabilityModelDescription") },
    { icon: Layers3, title: t("home.capabilitySkillTitle"), description: t("home.capabilitySkillDescription") },
    { icon: CloudCog, title: t("home.capabilityReadyTitle"), description: t("home.capabilityReadyDescription") },
  ];
  const highlights = [
    { icon: MonitorDown, title: t("home.highlightInstallTitle"), description: t("home.highlightInstallDescription") },
    { icon: KeyRound, title: t("home.highlightLoginTitle"), description: t("home.highlightLoginDescription") },
    { icon: Sparkles, title: t("home.highlightPortableTitle"), description: t("home.highlightPortableDescription") },
  ];
  const serviceItems = [
    { icon: Bot, title: t("home.serviceChatTitle"), description: t("home.serviceChatDescription") },
    { icon: Network, title: t("home.serviceSearchTitle"), description: t("home.serviceSearchDescription") },
    { icon: Image, title: t("home.serviceImageTitle"), description: t("home.serviceImageDescription") },
    { icon: Coins, title: t("home.serviceCreditsTitle"), description: t("home.serviceCreditsDescription") },
  ];
  const steps = [
    { icon: MonitorDown, title: t("home.stepDownloadTitle"), description: t("home.stepDownloadDescription") },
    { icon: KeyRound, title: t("home.stepLoginTitle"), description: t("home.stepLoginDescription") },
    { icon: Layers3, title: t("home.stepWorkTitle"), description: t("home.stepWorkDescription") },
  ];
  const selectedDownload = useMemo(() => pickDownloadOption(downloadOptions, selectedTarget), [downloadOptions, selectedTarget]);
  const downloadState: DownloadState = downloadLoading ? "loading" : downloadOptions.length > 0 ? "ready" : downloadError ? "error" : "empty";
  const latestVersion = downloadOptions[0]?.manifest.version;

  useEffect(() => {
    let disposed = false;
    const loadDownloads = async () => {
      setDownloadLoading(true);
      setDownloadError(false);
      const results = await Promise.allSettled(DOWNLOAD_TARGETS.map(async (target) => ({ target, manifest: await desktopReleaseApi.manifest(target) })));
      if (disposed) return;
      const nextOptions = dedupeDownloadOptions(results.flatMap((result) =>
        result.status === "fulfilled" && result.value.manifest ? [{ target: result.value.target, manifest: result.value.manifest }] : [],
      ));
      setDownloadOptions(nextOptions);
      setDownloadError(results.every((result) => result.status === "rejected"));
      setDownloadLoading(false);
    };
    void loadDownloads();
    return () => { disposed = true; };
  }, []);

  useEffect(() => {
    if (downloadOptions.length === 0 || downloadOptions.some((option) => sameTarget(option.target, selectedTarget))) return;
    const fallback = pickDownloadOption(downloadOptions, selectedTarget);
    if (fallback) setSelectedTarget(fallback.target);
  }, [downloadOptions, selectedTarget]);

  useEffect(() => {
    if (!location.hash) return;
    const sectionId = location.hash.slice(1);
    window.setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }, [location.hash]);

  const scrollToDownload = () => document.getElementById("download")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <main className="home-landing min-h-screen overflow-x-hidden bg-bg-page text-text-primary">
      <HomeHeader />
      <HomeHero highlights={highlights} latestVersion={latestVersion} onDownload={scrollToDownload} onLogin={() => { navigate("/login"); }} />
      <DownloadSection state={downloadState} options={downloadOptions} selectedTarget={selectedTarget} selectedDownload={selectedDownload} onTargetChange={setSelectedTarget} />
      <HomeSections capabilities={capabilities} latestVersion={latestVersion} onDownload={scrollToDownload} services={serviceItems} steps={steps} />
      <HomeFooter onDownload={scrollToDownload} />
    </main>
  );
};

export default HomePage;
